import React from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { moneyFormat } from "utils/money-format";
import { parseUsAddress } from "utils/us-address";

interface CreateDisbursementPreviewModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  isSubmitting?: boolean;
  charityEin: string;
  amount: number;
  purposeNote?: string;
  primaryDonorName?: string;
  jointDonorName?: string;
  donorAddress?: string;
  fundName?: string;
}

const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <TextField
    fullWidth
    disabled
    label={label}
    value={value}
    variant="outlined"
    InputLabelProps={{ shrink: true }}
    sx={{
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
      },
    }}
  />
);

const CreateDisbursementPreviewModal: React.FC<
  CreateDisbursementPreviewModalProps
> = ({
  open,
  onClose,
  onContinue,
  isSubmitting,
  charityEin,
  amount,
  purposeNote,
  primaryDonorName,
  jointDonorName,
  donorAddress,
  fundName,
}) => {
  const parsedAddress = donorAddress ? parseUsAddress(donorAddress) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-disbursement-preview-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600, md: 700 },
          maxHeight: "90vh",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Paper
          elevation={24}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              pb: 2,
            }}
          >
            <Typography
              id="create-disbursement-preview-modal-title"
              variant="h5"
              component="h2"
              fontWeight="medium"
            >
              Create Disbursement
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box
            sx={{
              p: 3,
              flex: 1,
              overflow: "auto",
            }}
          >
            <Stack spacing={3}>
              <ReadOnlyField label="Charity EIN" value={charityEin} />
              <ReadOnlyField
                label="Amount"
                value={`$${moneyFormat(amount)}`}
              />
              {purposeNote && (
                <ReadOnlyField label="Purpose" value={purposeNote} />
              )}
              {primaryDonorName && (
                <>
                  <ReadOnlyField
                    label="Primary Donor Name"
                    value={primaryDonorName}
                  />
                  {parsedAddress && (
                    <>
                      <ReadOnlyField
                        label="Primary Donor Address Line 1"
                        value={parsedAddress.line1}
                      />
                      {parsedAddress.line2 && (
                        <ReadOnlyField
                          label="Primary Donor Address Line 2"
                          value={parsedAddress.line2}
                        />
                      )}
                      <ReadOnlyField
                        label="Primary Donor City"
                        value={parsedAddress.city}
                      />
                      <ReadOnlyField
                        label="Primary Donor State"
                        value={parsedAddress.state}
                      />
                      <ReadOnlyField
                        label="Primary Donor Postal Code"
                        value={parsedAddress.postalCode}
                      />
                    </>
                  )}
                </>
              )}
              {jointDonorName && (
                <>
                  <ReadOnlyField
                    label="Joint Donor Name"
                    value={jointDonorName}
                  />
                  {parsedAddress && (
                    <>
                      <ReadOnlyField
                        label="Joint Donor Address Line 1"
                        value={parsedAddress.line1}
                      />
                      {parsedAddress.line2 && (
                        <ReadOnlyField
                          label="Joint Donor Address Line 2"
                          value={parsedAddress.line2}
                        />
                      )}
                      <ReadOnlyField
                        label="Joint Donor City"
                        value={parsedAddress.city}
                      />
                      <ReadOnlyField
                        label="Joint Donor State"
                        value={parsedAddress.state}
                      />
                      <ReadOnlyField
                        label="Joint Donor Postal Code"
                        value={parsedAddress.postalCode}
                      />
                    </>
                  )}
                </>
              )}
              {fundName && (
                <ReadOnlyField label="DAF Grant Fund Name" value={fundName} />
              )}
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              pt: 2,
              backgroundColor: "grey.50",
            }}
          >
            <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100 }}>
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={onContinue}
              disabled={isSubmitting}
              sx={{ minWidth: 120 }}
            >
              Continue
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default CreateDisbursementPreviewModal;
